import {DrawerRunner} from 'components/customer/Product/Specs/DrawerRunner';
import {FIELDSETS, FIELDSET_LAYOUT} from 'components/customer/Product/FieldSet';
import {useProductContext} from 'contexts/ProductContext';
import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {Col, Form, FormGroup, Row} from 'react-bootstrap';
import {Field} from 'shared/components/Forms/Field';
import {MoreInfo} from 'shared/components/MoreInfo/MoreInfo';
import excel from 'shared/Excel';
import {useFormikContext} from 'formik';
import {
    isDeviceSmall,
    isDeviceMid,
    isDeviceLarge,
    useTabletSize,
} from 'shared/helpers/DeviceSize';
import styled from 'styled-components';

const StyledLabel = styled.label`
    font-weight: bold;
    display: block;
    white-space: nowrap;
    margin-top: 9px;
`;

export const GenericFields = ({
    fields,
    fieldsetName,
    fieldsetLayout,
    fieldsetTitle = '',
    fieldSetIndex = null,
    fieldSetMoreInfo = null,
    selectHandler = false,
    indexedValues = false,
    isQFP = false,
    fieldsetOptions = false,
    hasFullWidth = false,
    colSpanFull = false,
    preview = false,
    cancelButton = null,
    formLabelSpanOverride = null,
    buttonSlot = null,
    stackFields = false,
}) => {
    const isSmallDevice = isDeviceSmall();
    const deviceIsMedium = isDeviceMid(false);
    const largeSizedDevice = isDeviceLarge();
    const isTabletSize = useTabletSize();

    const {getMaterialOptions, addInitialValue, isMiniBrowser} =
        useProductContext();
    const {values, setFieldTouched, setFieldValue} = useFormikContext();

    const materialOptions = getMaterialOptions();
    const [collapsed, setCollapsed] = useState(() => {
        // mainly used for qfp only
        if (fieldsetOptions.hasOwnProperty('collapsed')) {
            return excel.calculate(fieldsetOptions.collapsed, values);
        }

        return false;
    });

    const handleCollapseEvent = useCallback(() => {
        if (isQFP && fieldsetOptions.hasOwnProperty('collapsible')) {
            setCollapsed((collapsed) => !collapsed);
        }
    }, [isQFP, fieldsetOptions]);

    let span =
        isSmallDevice || deviceIsMedium || colSpanFull || isMiniBrowser
            ? 12
            : 8;

    if (fieldsetTitle == 'Inner Drawers') {
        span = 12;
    }

    let fieldsToStack = [];
    let fieldHeaderName = null;
    const formLabelSpan = formLabelSpanOverride || 5;

    const isFullWidth = [
        FIELDSETS.COMMENTS,
        FIELDSETS.SHELVES,
        FIELDSETS.DRILLINGS,
        FIELDSETS.EDGINGS,
        FIELDSETS.UPPER_SHEPVES,
        FIELDSETS.MIDDLE_SHELVES,
        FIELDSETS.LOWER_SHELVES,
        FIELDSETS.SIMPLE_SHELVES,
        FIELDSETS.SHELF_ADVANCED_CHECKBOX,
        FIELDSETS.ADDITIONAL,
    ];

    if (preview) {
        isFullWidth.push(FIELDSETS.FINGERPULL);
        isFullWidth.push(FIELDSETS.ADDITIONAL_SIZES);
        isFullWidth.push(FIELDSETS.OVEN);
        isFullWidth.push(FIELDSETS.RANGEHOOD);
        isFullWidth.push(FIELDSETS.MICROWAVE);
    }

    if (
        fieldsetName === FIELDSETS.DOOR_FIELDS &&
        materialOptions.cabinet_door &&
        materialOptions.cabinet_door.advanced
    ) {
        span = 12;
    }

    if (
        (isSmallDevice || isTabletSize) &&
        fieldsetName === FIELDSETS.VARIATIONS
    ) {
        span = 11;
    }

    if (isFullWidth.includes(fieldsetName)) {
        span = 12;
    }

    if (fieldsetLayout === FIELDSET_LAYOUT.COMPOUND) {
        span = 12;
    }

    if (fieldsetLayout === FIELDSET_LAYOUT.MID_RAILS) {
        span = 12;
    }

    if (isQFP || hasFullWidth) {
        span = 12;
    }

    if (isSmallDevice || !isQFP) {
        fieldsToStack = [
            'cabinet_include_assembly',
            'cabinet_include_hardware',
            'include_drawer_faces',
            'cabinet_adjustable_legs',
            'include_drawer_faces_label',
            'cabinet_comment',
        ];
    }

    useEffect(() => {
        if (isQFP) {
            fields &&
                fields.map((field) => {
                    addInitialValue(field.name, field.value);
                });
        }
    }, []);

    const formFields = useMemo(() => {
        return fields && Array.isArray(fields)
            ? fields.map((field, i) => {
                  if (field.name === 'drawer_runner_specs') {
                      return (
                          <DrawerRunner
                              key={field.name}
                              field={field}
                              reducedLabelColumnWidth
                          />
                      );
                  }

                  if (
                      typeof field.type === 'string' &&
                      field.type.indexOf('hidden') > -1
                  ) {
                      return <React.Fragment key={field.name} />;
                  }

                  let selectHandlerOverride;
                  let extraHandler;
                  if (field.name === 'fingerPull_styles') {
                      const fingerPullDefaultHandler = (
                          name,
                          value,
                          options
                      ) => {
                          const selectedOption = options.find(
                              (option) => option.id == value
                          );

                          if (selectedOption) {
                              // NOTE: calling both setFieldValue and setTouched
                              // is essential. Keep a lookout for function/way that
                              // might do both of this with single call.

                              // Use != null instead of truthy check so that
                              // zero values are applied correctly (e.g. when
                              // "None" resets topMargin to 0).
                              if (
                                  selectedOption.drawerGap != null &&
                                  values.hasOwnProperty('cabinet_drawer_gap')
                              ) {
                                  setFieldValue(
                                      'cabinet_drawer_gap',
                                      selectedOption.drawerGap
                                  );
                                  setFieldTouched('cabinet_drawer_gap', true);
                              }

                              if (
                                  selectedOption.topMargin != null &&
                                  values.hasOwnProperty('cabinet_drawer_top')
                              ) {
                                  setFieldValue(
                                      'cabinet_drawer_top',
                                      selectedOption.topMargin
                                  );
                                  setFieldTouched('cabinet_drawer_top', true);
                              }

                              if (
                                  selectedOption.topMargin != null &&
                                  values.hasOwnProperty('cabinet_door_top')
                              ) {
                                  setFieldValue(
                                      'cabinet_door_top',
                                      selectedOption.topMargin
                                  );
                                  setFieldTouched('cabinet_door_top', true);
                              }
                          }
                      };

                      selectHandlerOverride = (name, value, options) => {
                          fingerPullDefaultHandler(name, value, options);
                          setFieldValue(name, value);
                      };

                      extraHandler = fingerPullDefaultHandler;
                  } else {
                      selectHandlerOverride = selectHandler;
                  }

                  if (fieldsetName === FIELDSET_LAYOUT.DRAWER_EDGES) {
                      if (isQFP) {
                          span = 10;
                          fieldHeaderName = (
                              <Col xs="2">
                                  {field.name === 'drawer_edge_top' ? (
                                      <StyledLabel>
                                          Drawer {fieldSetIndex + 1}
                                      </StyledLabel>
                                  ) : null}
                              </Col>
                          );
                      } else {
                          span = 9;
                      }
                  }

                  return (
                      <Field
                          key={field.name}
                          field={field}
                          dataSource={
                              field.hasOwnProperty('dataSource')
                                  ? field.dataSource
                                  : false
                          }
                          fieldSpan={span}
                          formControlSpan={
                              isQFP &&
                              fieldsetOptions.hasOwnProperty('collapsible')
                                  ? 12
                                  : formLabelSpan
                          }
                          selectHandler={
                              selectHandlerOverride ||
                              (field.hasOwnProperty('action') && field.action)
                          }
                          extraHandler={extraHandler}
                          indexedValues={indexedValues}
                          fieldsetName={fieldsetName}
                          fieldSetIndex={fieldSetIndex}
                          isQFP={isQFP}
                          isStackFields={
                              stackFields ||
                              ['cabinet_note', ...fieldsToStack].includes(
                                  field.name
                              ) ||
                              (field.name == 'cabinet_include_hardware' &&
                                  isQFP)
                          }
                          cancelButton={cancelButton}
                          buttonSlot={buttonSlot}
                          fieldHeaderName={fieldHeaderName}
                      />
                  );
              })
            : null;
    }, [
        fields,
        indexedValues,
        values,
        selectHandler,
        fieldSetIndex,
        stackFields,
    ]);

    if (fieldsetLayout === FIELDSET_LAYOUT.COMPOUND && fieldsetTitle) {
        return (
            <Row>
                <Col md={deviceIsMedium || isMiniBrowser ? 12 : 9} lg={12}>
                    <FormGroup className="form-group">
                        <Form.Label className="fieldset-title">
                            {fieldsetTitle}
                        </Form.Label>
                        {formFields}
                    </FormGroup>
                </Col>
            </Row>
        );
    }

    if (fieldsetLayout === FIELDSET_LAYOUT.MID_RAILS && fieldsetTitle) {
        return (
            <Row>
                <Col sm={12}>
                    <FormGroup className="form-group">
                        <Form.Label
                            style={
                                isQFP
                                    ? {
                                          display: 'flex',
                                          position: 'relative',
                                          width: '50%',
                                          marginTop: 7,
                                      }
                                    : {}
                            }>
                            {fieldsetTitle}
                            {fieldSetMoreInfo ? (
                                <MoreInfo info={fieldSetMoreInfo} />
                            ) : null}
                        </Form.Label>
                        <hr style={{opacity: 0.15, marginTop: 0}} />
                        <div style={{paddingLeft: 8}}>{formFields}</div>
                    </FormGroup>
                </Col>
            </Row>
        );
    }

    if (
        isQFP &&
        fieldsetLayout !== FIELDSET_LAYOUT.COMPOUND &&
        fieldsetTitle &&
        fieldsetName != 'advanced_door_glass'
    ) {
        let colSpan = deviceIsMedium ? 6 : 2;

        if (
            fieldsetName === FIELDSETS.DOOR_FIELDS ||
            fieldsetName == FIELDSETS.DOOR_EDGES ||
            fieldsetName == FIELDSETS.MID_RAILS ||
            fieldsetName == FIELDSETS.MATERIAL_OPTIONS ||
            fieldsetName == FIELDSETS.BORDER_WIDTHS ||
            fieldsetName == FIELDSETS.COMMENTS ||
            fieldsetName == FIELDSETS.VERT_MID_RAILS ||
            fieldsetName == FIELDSETS.HORI_MID_RAILS
        ) {
            colSpan =
                fieldsetName === FIELDSETS.DOOR_FIELDS
                    ? 3
                    : largeSizedDevice
                      ? colSpan
                      : 3;
        }

        if (fieldsetName == FIELDSETS.ADDITIONAL) {
            colSpan = largeSizedDevice ? 4 : 5;
        }

        if (fieldsetName == FIELDSETS.DRAWER_FIELDS) {
            colSpan = largeSizedDevice ? 3 : 4;
        }

        return (
            <Col sm={colSpan}>
                <div
                    className="fieldset-title"
                    onClick={handleCollapseEvent}
                    style={{
                        cursor: fieldsetOptions.hasOwnProperty('collapsible')
                            ? 'pointer'
                            : 'initial',
                        userSelect: 'none',
                    }}>
                    <strong>
                        {fieldsetTitle}{' '}
                        {fieldsetOptions.hasOwnProperty('collapsible')
                            ? ` (Click to ${collapsed ? 'Expand' : 'Collapse'})`
                            : ''}
                    </strong>
                </div>
                <br />
                {collapsed ? null : formFields}
            </Col>
        );
    }

    return formFields;
};
